import React, { useEffect, useRef, useState } from 'react';
import { Award, Gem, Heart, History, Users, ArrowRight, ShieldCheck, Map, Clock, Truck } from 'lucide-react';
import { SiteSettings } from '../types';

interface AboutProps {
  onNavigate: (page: string) => void;
  settings?: SiteSettings;
}

const About: React.FC<AboutProps> = ({ onNavigate, settings }) => {
  const [scrollY, setScrollY] = useState(0);

  const address = settings?.address || 'Luxury Lane, Makati';
  const phone = settings?.phone || '0945 392 8946';
  const hours = settings?.hours || 'Mon - Sat: 10am - 7pm';

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="bg-stone-50 min-h-screen text-stone-900 overflow-hidden">
      
      {/* 1. CINEMATIC HERO */}
      <section className="relative h-[80vh] w-full overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0">
          {/* Updated Hero Image: Luxury Jewelry Store Interior/Display */}
          <img 
            src="https://images.unsplash.com/photo-1522312346375-d1a52e2b99b3?auto=format&fit=crop&w=2070&q=80" 
            alt="Jewelry Workshop" 
            className="w-full h-full object-cover animate-[zoomSlow_25s_infinite_alternate]"
          />

          {/* Enhanced Overlay for Readability */}
          <div className="absolute inset-0 bg-stone-900/50 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-transparent to-stone-900/40"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <span className="inline-block text-gold-300 text-xs tracking-[0.4em] uppercase mb-6 animate-fade-in font-bold drop-shadow-md">
            The Maison
          </span>
          <h1 className="font-serif text-5xl md:text-7xl text-white mb-8 leading-tight drop-shadow-2xl animate-fade-in delay-100">
            A Legacy of <br/>
            <span className="italic text-gold-200">Perfection</span>
          </h1>
          <p className="text-white/90 text-lg font-light max-w-xl mx-auto leading-relaxed animate-fade-in delay-200 backdrop-blur-md bg-white/10 p-6 border border-white/20 rounded-sm shadow-xl">
            For over four decades, Sparkling Gems has defined the standard of luxury in the Philippines, curating treasures that transcend time.
          </p>
        </div>
      </section>

      {/* 2. BRAND TICKER */}
      <div className="bg-stone-900 py-6 border-y border-gold-600/30 overflow-hidden whitespace-nowrap relative z-20">
        <div className="inline-block animate-[scroll_30s_linear_infinite] text-gold-400/80 text-xs tracking-[0.4em] uppercase font-medium">
           <span>Established 1985 • Manila • </span>
           <span className="mx-12">GIA Alumni Members • </span>
           <span className="mx-12">Conflict-Free Diamonds • </span>
           <span className="mx-12">Bespoke Artisanship • </span>
           <span className="mx-12">Heritage Restoration • </span>
           <span>Established 1985 • Manila • </span>
           <span className="mx-12">GIA Alumni Members • </span>
           <span className="mx-12">Conflict-Free Diamonds • </span>
           <span className="mx-12">Bespoke Artisanship • </span>
           <span className="mx-12">Heritage Restoration • </span>
        </div>
      </div>

      {/* 3. OUR STORY - ALTERNATING LAYOUT */}
      <section className="py-32 px-6 max-w-7xl mx-auto">
        {/* Section 1 */}
        <div className="flex flex-col md:flex-row items-center gap-16 mb-32 group">
          <div className="w-full md:w-1/2 relative">
            <div className="absolute top-4 -left-4 w-full h-full border border-gold-200 transition-transform duration-700 group-hover:translate-x-2 group-hover:translate-y-2"></div>
            <div className="relative overflow-hidden aspect-[4/5] bg-stone-200">
              {/* Updated Image: Jeweler working / Hands */}
              <img 
                src="https://images.unsplash.com/photo-1626784215021-2e39ccf971cd?q=80&w=2070&auto=format&fit=crop" 
                alt="The Founder" 
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105 filter sepia-[0.2]" 
              />
            </div>
          </div>
          <div className="w-full md:w-1/2 text-center md:text-left">
            <div className="flex items-center gap-4 justify-center md:justify-start mb-6 text-gold-600">
               <History size={24} strokeWidth={1} />
               <span className="text-xs tracking-[0.3em] uppercase font-bold">The Beginning</span>
            </div>
            <h2 className="font-serif text-4xl text-stone-900 mb-6">1985: A Humble Start</h2>
            <p className="text-stone-600 font-light leading-relaxed mb-6">
              It began with a single magnifying loupe and a passion for brilliance. Our founder, traveling through the gem markets of Southeast Asia, envisioned a maison in Manila that would rival the great jewelers of Europe. 
            </p>
            <p className="text-stone-600 font-light leading-relaxed">
              What started as a small workshop has grown into a sanctuary for collectors, yet the core principle remains: every stone must tell a story.
            </p>
            <div className="mt-8">
            </div>
          </div>
        </div>

        {/* Section 2 */}
        <div className="flex flex-col md:flex-row-reverse items-center gap-16 group">
          <div className="w-full md:w-1/2 relative">
             <div className="absolute bottom-4 -right-4 w-full h-full border border-stone-300 transition-transform duration-700 group-hover:-translate-x-2 group-hover:-translate-y-2"></div>
            <div className="relative overflow-hidden aspect-[4/3] bg-stone-200">
              <img 
                src="https://images.unsplash.com/photo-1522312346375-d1a52e2b99b3?auto=format&fit=crop&w=2148&q=80" 
                alt="Modern Showroom" 
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" 
              />
            </div>
          </div>
          <div className="w-full md:w-1/2 text-center md:text-left">
            <div className="flex items-center gap-4 justify-center md:justify-start mb-6 text-gold-600">
               <Gem size={24} strokeWidth={1} />
               <span className="text-xs tracking-[0.3em] uppercase font-bold">The Evolution</span>
            </div>
            <h2 className="font-serif text-4xl text-stone-900 mb-6">Curating the Extraordinary</h2>
            <p className="text-stone-600 font-light leading-relaxed mb-6">
              Today, Sparkling Gems is not just a retailer but a curator of investment-grade assets. We blend traditional craftsmanship with modern technology, using 3D modeling for bespoke commissions while maintaining hand-forged techniques for assembly.
            </p>
            <p className="text-stone-600 font-light leading-relaxed">
              Our curated collection now includes rare vintage timepieces and designer handbags, authenticated by our in-house experts.
            </p>
          </div>
        </div>
      </section>

      {/* 4. PARALLAX ATELIER SECTION */}
      {/* Changed background image to be a distinct photo of jewelry tools/craftsmanship to emphasize 'Authenticity' */}
     <section
  className="relative py-40 bg-fixed bg-center bg-cover"
  style={{
    backgroundImage:
      "url('https://images.unsplash.com/photo-1600180758895-7d7f6d7b4f5c?auto=format&fit=crop&w=2070&q=80')",
  }}
>
  <div className="absolute inset-0 bg-stone-900/75"></div>
  <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>

  <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-white">
    <ShieldCheck
      size={48}
      className="mx-auto text-gold-400 mb-8 animate-pulse"
      strokeWidth={1}
    />
    <h2 className="font-serif text-5xl md:text-6xl mb-8">
      The Pledge of Authenticity
    </h2>
    <p className="text-xl font-light text-stone-200 leading-relaxed max-w-3xl mx-auto">
      "In an age of mass production, we promise the rare. Every diamond is GIA
      certified. Every vintage piece is rigorously vetted. We sell nothing we
      would not be proud to own ourselves."
    </p>
          <div className="mt-12 flex justify-center gap-8 text-xs uppercase tracking-widest text-gold-400">
             <div className="flex flex-col items-center gap-3">
               <div className="w-16 h-16 rounded-full border border-gold-500/50 flex items-center justify-center bg-white/5 backdrop-blur">100%</div>
               <span>Certified</span>
             </div>
             <div className="flex flex-col items-center gap-3">
               <div className="w-16 h-16 rounded-full border border-gold-500/50 flex items-center justify-center bg-white/5 backdrop-blur">Lifetime</div>
               <span>Warranty</span>
             </div>
             <div className="flex flex-col items-center gap-3">
               <div className="w-16 h-16 rounded-full border border-gold-500/50 flex items-center justify-center bg-white/5 backdrop-blur">Global</div>
               <span>Shipping</span>
             </div>
          </div>
        </div>
      </section>

      {/* 5. INTERACTIVE VALUES GRID */}
      <section className="py-32 bg-white relative">
        <div className="absolute top-0 left-0 w-full h-full bg-noise opacity-30 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
             <span className="text-gold-600 text-xs tracking-[0.3em] uppercase font-bold">Our Philosophy</span>
             <h2 className="font-serif text-4xl mt-4">Why We Exist</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                icon: <Award size={32} />, 
                title: "Excellence", 
                desc: "We do not compromise. From the cut of the gem to the polish of the metal, 'good enough' is never enough." 
              },
              { 
                icon: <Users size={32} />, 
                title: "Relationship", 
                desc: "We are not just selling objects; we are marking milestones. We view every client as a lifelong partner." 
              },
              { 
                icon: <Heart size={32} />, 
                title: "Passion", 
                desc: "Jewelry is emotional. We pour our heart into sourcing pieces that evoke wonder and delight." 
              }
            ].map((item, idx) => (
              <div key={idx} className="group p-10 border border-stone-100 bg-stone-50 hover:bg-stone-900 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl text-center md:text-left">
                <div className="w-16 h-16 bg-white border border-stone-200 flex items-center justify-center text-stone-900 mb-8 group-hover:bg-gold-600 group-hover:text-white group-hover:border-gold-600 transition-colors duration-300 shadow-sm mx-auto md:mx-0">
                  {item.icon}
                </div>
                <h3 className="font-serif text-2xl mb-4 group-hover:text-white transition-colors">{item.title}</h3>
                <p className="text-stone-500 font-light leading-relaxed group-hover:text-stone-300 transition-colors">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. SHIPPING INFORMATION SECTION - ADDED HERE */}
      <section className="py-16 bg-stone-50 border-y border-stone-200">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-8 bg-white p-8 rounded-sm shadow-sm border border-stone-100">
            <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center text-gold-600 flex-shrink-0">
              <Truck size={32} />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="font-serif text-2xl text-stone-900 mb-2">Shipping Information</h3>
              <p className="text-stone-600 mb-3">
                We offer shipping <span className="font-bold text-gold-600">nationwide and worldwide</span>. 
                All orders are carefully packaged, insured, and tracked for your peace of mind.
              </p>
              <div className="flex flex-wrap justify-center md:justify-start gap-6 text-sm mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gold-600 rounded-full"></div>
                  <span className="text-stone-700"><span className="font-bold">Nationwide:</span> 3-5 business days</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gold-600 rounded-full"></div>
                  <span className="text-stone-700"><span className="font-bold">Worldwide:</span> 7-14 business days</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gold-600 rounded-full"></div>
                  <span className="text-stone-700"><span className="font-bold">Insurance:</span> Included on all orders</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. LOCATION / VISIT */}
      <section className="py-24 px-6 bg-white text-center">
         <div className="max-w-3xl mx-auto border border-gold-200 p-12 relative">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4">
               <Map size={32} className="text-gold-500" strokeWidth={1} />
            </div>
            <h2 className="font-serif text-4xl mb-6">Visit Our Atelier</h2>
            <p className="text-stone-500 mb-8 font-light">
               Experience our collection in person. Private viewings are available by appointment to ensure you receive our undivided attention.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-sm text-stone-800 mb-8">
               <div>
                  <h4 className="font-bold uppercase tracking-widest mb-2">Manila</h4>
                  <p className="font-light">{address}<br/>{phone}</p>
               </div>
               <div>
                  <h4 className="font-bold uppercase tracking-widest mb-2">Hours</h4>
                  <p className="font-light">{hours}</p>
               </div>
            </div>
            <button 
              onClick={() => onNavigate('contact')}
              className="bg-stone-900 text-white px-8 py-3 uppercase text-xs tracking-[0.2em] hover:bg-gold-600 transition-colors"
            >
               Book Appointment
            </button>
         </div>
      </section>

      {/* Footer note */}
      <div className="bg-stone-900 text-stone-400 text-center py-6 text-xs tracking-widest">
        <p>© {new Date().getFullYear()} Sparkling Gems. All rights reserved. Shipping nationwide and worldwide.</p>
      </div>
    </div>
  );
};

export default About;